import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ReportesMejorado = () => {
  const [ventas, setVentas] = useState([]);
  const [rango, setRango] = useState('hoy');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [reporte, setReporte] = useState({});
  const [graficoData, setGraficoData] = useState([]);
  const [cerrado, setCerrado] = useState(false);

  // Para Reporte Z (solo día seleccionado)
  const [fechaZ, setFechaZ] = useState(new Date().toISOString().substr(0, 10));

  useEffect(() => {
    const fetchData = async () => {
      const ventasSnapshot = await getDocs(collection(db, 'ventas'));
      const ventasData = ventasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha.toDate()
      }));
      setVentas(ventasData);
    };
    fetchData();
    checkCierreZ();
    // eslint-disable-next-line
  }, []);

  // --- Botones Reporte X y Z: Usan ventas del día actual ---
  const checkCierreZ = async () => {
    const q = query(
      collection(db, "cierresZ"),
      where("fecha", "==", fechaZ)
    );
    const snap = await getDocs(q);
    setCerrado(!snap.empty);
  };

  const ventasDelDia = ventas.filter(v => {
    const f = v.fecha.toISOString().slice(0, 10);
    return f === fechaZ;
  });

  const resumenPorFormaPago = () => {
    const resumen = { efectivo: 0, punto: 0, transferencia: 0 };
    ventasDelDia.forEach(v => {
      if (resumen[v.formaPago]) resumen[v.formaPago] += Number(v.total);
    });
    return resumen;
  };

  // --- Tu lógica de reporte general ---
  const filtrarPorRango = () => {
    const ahora = new Date();
    let desde = new Date();
    let hasta = new Date();
    if (rango === 'semana') {
      desde.setDate(ahora.getDate() - 7);
    } else if (rango === 'mes') {
      desde.setMonth(ahora.getMonth() - 1);
    } else if (rango === 'rango') {
      desde = new Date(fechaInicio);
      hasta = new Date(fechaFin);
    } else {
      desde.setHours(0, 0, 0, 0);
    }
    return ventas.filter(v => {
      const fecha = new Date(v.fecha);
      if (rango === 'rango') {
        return fecha >= desde && fecha <= hasta;
      }
      return fecha >= desde;
    });
  };

  const calcularReporte = () => {
    const datos = filtrarPorRango();
    const resumen = {
      totalUSD: 0,
      totalBS: 0,
      vendedores: {},
    };
    const agrupadosPorDia = {};

    datos.forEach(v => {
      const fechaKey = v.fecha.toISOString().split('T')[0];
      const tasa = v.tasaBCV || 70;
      const montoUSD = v.moneda === 'USD' ? v.total : v.total / tasa;
      const montoBS = v.moneda === 'BS' ? v.total : v.total * tasa;

      resumen.totalUSD += montoUSD;
      resumen.totalBS += montoBS;

      if (!resumen.vendedores[v.vendedor]) resumen.vendedores[v.vendedor] = 0;
      resumen.vendedores[v.vendedor] += montoUSD;

      if (!agrupadosPorDia[fechaKey]) agrupadosPorDia[fechaKey] = { fecha: fechaKey, USD: 0, BS: 0 };
      agrupadosPorDia[fechaKey].USD += montoUSD;
      agrupadosPorDia[fechaKey].BS += montoBS;
    });

    const dataGrafico = Object.values(agrupadosPorDia).sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
    setGraficoData(dataGrafico);
    setReporte(resumen);
  };

  useEffect(() => {
    calcularReporte();
    // eslint-disable-next-line
  }, [ventas, rango, fechaInicio, fechaFin]);

  const exportarExcel = () => {
    const datos = filtrarPorRango().map(v => ({
      Fecha: v.fecha.toLocaleString(),
      Cliente: v.clienteId,
      Total: v.total.toFixed(2),
      Moneda: v.moneda,
      Vendedor: v.vendedor,
    }));
    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ventas');
    XLSX.writeFile(wb, `Reporte_Ventas_${rango}.xlsx`);
  };

  // -------- Reporte X/Z PDF --------
  const generarPDF = (tipo) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Global-Multiservicios", 10, 12);
    doc.setFontSize(12);
    doc.text(`Reporte ${tipo} - ${fechaZ}`, 10, 22);
    doc.text(`Total operaciones: ${ventasDelDia.length}`, 10, 29);

    const resumen = resumenPorFormaPago();
    autoTable(doc, {
      startY: 36,
      head: [["Forma de Pago", "Monto ($)"]],
      body: [
        ["Efectivo", resumen.efectivo.toFixed(2)],
        ["Punto", resumen.punto.toFixed(2)],
        ["Transferencia", resumen.transferencia.toFixed(2)],
      ],
    });
    doc.save(`Reporte_${tipo}_${fechaZ}.pdf`);
  };

  const handleReporteZ = async () => {
    if (cerrado) {
      alert("¡Ya fue cerrado el día!");
      return;
    }
    if (!window.confirm("¿Deseas cerrar el día? Esto no se podrá deshacer.")) return;
    generarPDF('Z');
    await addDoc(collection(db, "cierresZ"), {
      fecha: fechaZ,
      generadoEn: new Date(),
      totalVentas: ventasDelDia.length,
    });
    setCerrado(true);
    alert("Día cerrado exitosamente (Reporte Z generado).");
  };

  return (
    <div className="reportes-container">
      <div className="reportes-header">
        <h2>Reporte General de Ventas</h2>
        <button className="btn-exportar" onClick={exportarExcel}>Exportar a Excel</button>
      </div>

      <div className="filtros-reportes">
        <label>
          Rango:
          <select value={rango} onChange={(e) => setRango(e.target.value)}>
            <option value="hoy">Hoy</option>
            <option value="semana">Últimos 7 días</option>
            <option value="mes">Últimos 30 días</option>
            <option value="rango">Personalizado</option>
          </select>
        </label>
        {rango === 'rango' && (
          <>
            <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
            <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
          </>
        )}
      </div>

      <div className="reporte-card">
        <h3>Total Vendido: ${reporte.totalUSD?.toFixed(2) || '0.00'} | Bs {reporte.totalBS?.toFixed(2) || '0.00'}</h3>
      </div>

      <div className="reporte-card">
        <h3>Ventas por Día</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={graficoData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
            <XAxis dataKey="fecha" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="USD" fill="#00C49F" name="USD" />
            <Bar dataKey="BS" fill="#FF8042" name="BS" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="reporte-card">
        <h3>Ventas por Vendedor (USD)</h3>
        <ul>
          {Object.entries(reporte.vendedores || {}).map(([nombre, total]) => (
            <li key={nombre}>{nombre}: ${total.toFixed(2)}</li>
          ))}
        </ul>
      </div>

      {/* BOTONES DE REPORTE X Y Z */}
      <div className="reporte-card">
        <h3>Reporte Diario Fiscal (X / Z)</h3>
        <label>
          Fecha:
          <input type="date" value={fechaZ} onChange={e => { setFechaZ(e.target.value); setCerrado(false); }} />
        </label>
        <p>¿Día cerrado?: <b style={{ color: cerrado ? "red" : "green" }}>{cerrado ? "Sí" : "No"}</b></p>
        <button onClick={() => generarPDF('X')} disabled={!ventasDelDia.length}>
          Generar Reporte X
        </button>{" "}
        <button
          onClick={handleReporteZ}
          disabled={!ventasDelDia.length || cerrado}
          style={{ background: cerrado ? "#ccc" : "#f60", color: "#fff", marginLeft: 10 }}
        >
          Generar Reporte Z
        </button>
      </div>
    </div>
  );
};

export default ReportesMejorado;
